import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { WorkflowForm, WorkflowFormComponent, WorkflowFormKey, WorkflowTaskDataService } from '@lib/workflow-integration';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import {
    IPurchaseRequisitionTaskPreview,
    PurchaseRequisitionTaskPreview,
} from '../../types/PurchaseRequisitionTaskPreview';
import { IPurchaseRequisitionTask, PurchaseRequisitionTask } from '../../types/PurchaseRequisitionTask';
import { DemandDataService } from '../../../demand/demand-data.service';
import {
    createDeeplinkCompletionContext,
    CustomRouter,
    DeeplinkCompletionAction,
    DeeplinkCompletionContextService,
    NotificationMessageLevel,
    NotificationService,
} from '@lib/core';
import { PurchaseRequisitionDetailComponent } from '../../../purchase-requisition/purchase-requisition-detail/purchase-requisition-detail.component';
import { ActivatedRoute } from '@angular/router';
import { PurchaseRequisitionDataService } from '../../../purchase-requisition/purchase-requisition-data.service';
import { PurchaseRequisitionStatusEnum } from '../../../purchase-requisition/types/PurchaseRequistionStatusEnum';
import { CommentDataService } from '@lib/comment';
import { ItemClassificationDataService } from '@lib/classification';
import { FileDataService } from '@lib/file';
import { I18nService } from '@lib/i18n';
import { HttpErrorResponse } from '@angular/common/http';
import { DemandValidationError, IDemandValidationError } from '../../../demand/validation/types/IDemandValidationError';
import { ValidationSubject } from '../../../demand/validation/types/IValidationError';

@WorkflowForm(
    marker('Bedarfsmeldung überarbeiten'),
    [WorkflowFormKey.PURCHASE_REQUISITION_REWORK_FORM],
    (dto: IPurchaseRequisitionTaskPreview, displayName: string) => new PurchaseRequisitionTaskPreview(dto, displayName),
    marker('Bedarfsmeldung überarbeiten'),
)
@Component({
    selector: 'rework-purchase-requisition-task',
    templateUrl: './rework-purchase-requisition-task.component.html',
    styleUrls: ['./rework-purchase-requisition-task.component.scss'],
    standalone: false,
})
export class ReworkPurchaseRequisitionTaskComponent extends WorkflowFormComponent implements OnInit {
    private readonly taskDataService = inject(WorkflowTaskDataService);
    private readonly demandDataService = inject(DemandDataService);
    private readonly commentDataService = inject(CommentDataService);
    private readonly itemClassificationDataService = inject(ItemClassificationDataService);
    private readonly fileDataService = inject(FileDataService);
    private readonly router = inject(CustomRouter);
    private readonly route = inject(ActivatedRoute);
    private readonly purchaseRequisitionDataService = inject(PurchaseRequisitionDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly i18n = inject(I18nService);
    private readonly deeplinkCompletionContext = inject(DeeplinkCompletionContextService);

    @ViewChild('purchaseRequisitionDetailComponent')
    purchaseRequisitionDetailComponent?: PurchaseRequisitionDetailComponent;
    public task?: PurchaseRequisitionTask;

    public get completionBlocked(): boolean {
        return !this.purchaseRequisitionDetailComponent?.isValid || this.isCompleted;
    }

    public withdraw(): void {
        if (
            this.task &&
            PurchaseRequisitionStatusEnum.PENDING === this.task.purchaseRequisition.status.value &&
            !this._isCompleted
        ) {
            this._isCompleted = true;
            this.purchaseRequisitionDataService.withdraw(this.task.purchaseRequisition.id).subscribe({
                next: () => {
                    this.deeplinkCompletionContext.completeRun(
                        createDeeplinkCompletionContext(
                            DeeplinkCompletionAction.PURCHASE_REQUISITION_WITHDRAWN,
                            this.task!.purchaseRequisition.number,
                        ),
                    );
                    const successMessage = marker('Bedarfsmeldung zurückgezogen.');
                    this.notificationService.showNotification(successMessage, NotificationMessageLevel.SUCCESS);
                    if (this.routeToListAfterCompletion) {
                        this.router.navigate([this.router.getViewFromRoute(), 'task', 'list'], { replaceUrl: true });
                    }
                },
                error: () => (this._isCompleted = false),
            });
        }
    }

    public async save(): Promise<void> {
        if (this.task && !this.completionBlocked) {
            this._isCompleted = true;
            await this.task.purchaseRequisition.demand.saveMetaData();
            this.taskDataService.completeTask(this.task.id, {}).subscribe({
                next: () => {
                    this.deeplinkCompletionContext.completeRun(
                        createDeeplinkCompletionContext(
                            DeeplinkCompletionAction.PURCHASE_REQUISITION_REWORK_COMPLETED,
                            this.task!.purchaseRequisition.number,
                        ),
                    );
                    this.completed.emit();
                    if (this.routeToListAfterCompletion) {
                        this.router.navigate([this.router.getViewFromRoute(), 'task', 'list'], { replaceUrl: true });
                    }
                },
                error: (error: HttpErrorResponse) => {
                    this._isCompleted = false;

                    const validationError: IDemandValidationError | undefined = error?.error?.error;
                    if (
                        validationError &&
                        validationError.subject === ValidationSubject.DEMAND &&
                        this.task?.purchaseRequisition
                    ) {
                        this.task.purchaseRequisition.demand.setValidationError(
                            new DemandValidationError(validationError, this.i18n),
                        );
                    }
                },
            });
        }
    }

    ngOnInit(): void {
        this.taskId = this.taskId ? this.taskId : this.route.snapshot.params['taskId'];
        if (this.taskId) {
            this.getTask(this.taskId);
        }
    }

    public getTask(taskId: string): void {
        this.taskDataService
            .getSingleTaskById<IPurchaseRequisitionTask>(taskId)
            .subscribe(
                (task: IPurchaseRequisitionTask) =>
                    (this.task = new PurchaseRequisitionTask(
                        task,
                        this.demandDataService,
                        this.commentDataService,
                        this.itemClassificationDataService,
                        this.fileDataService,
                        this.i18n,
                    )),
            );
    }
}
